import { AnchorResolver } from '../../models/layout/AnchorResolver';
import { Scene } from '../../models/layout/Scene';

import type { Element } from '../../models/Element';
import type { ElementAnchor } from '../../models/layout/types';
import type { Timeline } from '../../models/Timeline';

/**
 * Re-anchors scenes onto elements that are about to survive an element removal,
 * dropping the ones nothing is left to anchor to.
 *
 * Scene anchors hold element ids, and {@link AnchorResolver} **throws** on an id
 * the timeline no longer holds — so a removal that leaves an anchor pointing at
 * a departed element does not merely mislay a layout, it takes the whole preview
 * serialization down (`toPreviewJSON` → `applyLayoutSegmentsToScenes`). Any
 * command that removes elements owns this pass.
 *
 * Must run **before** the elements come off the timeline: the overlap test needs
 * the scene's resolved time range, which is only resolvable while the elements
 * its anchors name are still there.
 *
 * @param scenes The timeline's scenes as they stand now
 * @param keptElementIds Every element id that will still exist afterwards
 * @param timeline The timeline, still holding the elements about to be removed
 * @returns The scenes to keep, with invalid anchors moved onto surviving
 *   elements **on the track the anchor already named**, so a transcript-anchored
 *   scene is never re-anchored onto audio or b-roll; a scene with nothing left
 *   on its own track is dropped
 */
export function clampSceneAnchors(
  scenes: readonly Scene[],
  keptElementIds: ReadonlySet<string>,
  timeline: Timeline,
): Scene[] {
  const anchorResolver = new AnchorResolver();

  // Build element-to-track map so we can constrain replacement anchors
  // to the same track as the original anchor's element
  const elementTrackMap = new Map<string, string>();
  for (const track of timeline.tracks) {
    for (const el of track.elements) {
      elementTrackMap.set(el.id, track.id);
    }
  }

  // Get all kept elements sorted by time
  const keptElements = timeline.tracks
    .flatMap(track => track.elements)
    .filter(el => keptElementIds.has(el.id))
    .sort((a, b) => a.time - b.time);

  if (keptElements.length === 0) {
    return [];
  }

  return scenes
    .map(scene => {
      const startIsString = typeof scene.anchorStart === 'string';
      const endIsString = typeof scene.anchorEnd === 'string';
      const startValid =
        startIsString || keptElementIds.has(scene.anchorStart.elementId);
      const endValid =
        endIsString || keptElementIds.has(scene.anchorEnd.elementId);

      // Both valid - no change needed
      if (startValid && endValid) {
        return scene;
      }

      // Resolve scene time range (while elements still exist)
      let sceneStartTime: number;
      let sceneEndTime: number;
      try {
        sceneStartTime = anchorResolver.resolve(scene.anchorStart, timeline);
        sceneEndTime = anchorResolver.resolve(scene.anchorEnd, timeline);
      } catch {
        // Can't resolve anchors - scene references elements that don't exist
        return null;
      }

      // Find kept elements that overlap with this scene's time range
      const overlappingKeptElements = keptElements.filter(
        el => el.time < sceneEndTime && el.end_time > sceneStartTime,
      );

      if (overlappingKeptElements.length === 0) {
        // No kept elements overlap with this scene - remove it
        return null;
      }

      // A replacement anchor may only come from the track the original anchor
      // named. Constrained, not preferred: a scene describes a layout over one
      // track's material, so re-anchoring a transcript-anchored scene onto
      // audio or b-roll would have it claim a span it never covered. Music is
      // the case that makes this bite — it is exempt from a reel Section's
      // contents by design (DE7, it runs the whole reel), so it survives every
      // removal and overlaps everything, and a scene wholly inside the
      // departing passage would find it and stretch onto it instead of going.
      // With nothing left on its own track the scene has nothing to describe.
      const survivingOnTrackOf = (anchor: ElementAnchor): Element[] => {
        const originalTrackId = elementTrackMap.get(anchor.elementId);
        if (!originalTrackId) return [];
        return overlappingKeptElements.filter(
          el => elementTrackMap.get(el.id) === originalTrackId,
        );
      };

      let newAnchorStart = scene.anchorStart;
      if (!startValid && typeof scene.anchorStart !== 'string') {
        const candidates = survivingOnTrackOf(scene.anchorStart);
        if (candidates.length === 0) {
          return null;
        }
        newAnchorStart = {
          elementId: candidates[0].id,
          relativeTime: null,
          end: false,
        };
      }

      let newAnchorEnd = scene.anchorEnd;
      if (!endValid && typeof scene.anchorEnd !== 'string') {
        const candidates = survivingOnTrackOf(scene.anchorEnd);
        if (candidates.length === 0) {
          return null;
        }
        newAnchorEnd = {
          elementId: candidates[candidates.length - 1].id,
          relativeTime: null,
          end: true,
        };
      }

      return new Scene({
        id: scene.id,
        anchorStart: newAnchorStart,
        anchorEnd: newAnchorEnd,
        layout: scene.layout.clone(),
      });
    })
    .filter((scene): scene is Scene => scene !== null);
}
